import { createSelector } from 'reselect';
import { getTextValue, getValue } from '../constants/getFieldValues';
import { isFlatValid, notEmptyField } from '../validators/common';
import { AddressFields } from '../../types/address';
import {
  isAddressSubsequentErrorVisible,
  isOMKErrorVisible,
} from './addressUnmodeled';
import { setErrorField } from '../constants/errors';
import { isNewMoscowArea, ZEL_AO } from '../constants/address';
import { getAddressValueFromSuggest } from '../helpers/address';

export const getAddress: ({ address }) => AddressFields = ({ address }) =>
  address;

export const getIsAddressCompleted: (state: {
  address: AddressFields;
}) => boolean = createSelector(
  getAddress,
  ({ isAddressCompleted }) => isAddressCompleted
);

export const getAddressArea: (state: {
  address: AddressFields;
}) => string = createSelector(getAddress, ({ area }) => getTextValue(area));

export const isZelArea: (state: {
  address: AddressFields;
}) => boolean = createSelector(getAddressArea, (area) => area === ZEL_AO);

export const getAddressFlat: (state: {
  address: AddressFields;
}) => string = createSelector(getAddress, ({ flat }) => getValue(flat));

export const getAddressAddress: (state: {
  address: AddressFields;
}) => string = createSelector(getAddress, ({ address }) => getValue(address));

export const getAddressSuggestions: (state: {
  address: AddressFields;
}) => Array<any> = createSelector(getAddress, ({ suggestions }) => suggestions);

export const fullAddressWithFlat: (state: {
  address: AddressFields;
}) => string = createSelector(
  getAddress,
  getAddressFlat,
  ({ address }, flat) => {
    const replacedAddress = getValue(address).replace('Город Москва', 'Москва');
    return flat ? `${replacedAddress}, Квартира/офис ${flat}` : replacedAddress;
  }
);

export const getAddressDistrict: (state: {
  address: AddressFields;
}) => string = createSelector(getAddress, ({ district }) =>
  getTextValue(district)
);

export const getAddressStreet: (state: {
  address: AddressFields;
}) => string = createSelector(getAddress, ({ street }) => getValue(street));

export const getAddressStreetIsFoundInCatalog: (state: {
  address: AddressFields;
}) => boolean = createSelector(getAddress, ({ street }) =>
  Boolean(street.isFoundInCatalog)
);

export const getAddressHouse: (state: {
  address: AddressFields;
}) => string = createSelector(getAddress, ({ house }) => getValue(house));

export const getAddressBuilding: (state: {
  address: AddressFields;
}) => string = createSelector(getAddress, ({ building }) => getValue(building));

export const getAddressIsManualInput: (state: {
  address: AddressFields;
}) => boolean = createSelector(
  getAddress,
  ({ isManualInput }) => isManualInput
);

export const validateAddressArea: (state: {
  address: AddressFields;
}) => boolean = createSelector(
  getAddressArea,
  getAddressIsManualInput,
  (area, isManualInput) => isManualInput && notEmptyField(area)
);

export const validateAddressFlat: (state: {
  address: AddressFields;
}) => boolean = createSelector(getAddressFlat, isFlatValid);

export const validateAddressDistrict: (state: {
  address: AddressFields;
}) => boolean = createSelector(
  getAddressDistrict,
  getAddressIsManualInput,
  (district, isManualInput) => isManualInput && notEmptyField(district)
);

export const validateAddressStreet: (state: {
  address: AddressFields;
}) => boolean = createSelector(
  getAddress,
  getAddressArea,
  getAddressSuggestions,
  getAddressStreet,
  isZelArea,
  getAddressStreetIsFoundInCatalog,
  (address, area, suggestions, street, isZelArea, streetIsFoundInCatalog) => {
    if (address.isAddressCompleted) return false;
    if (isNewMoscowArea(area)) return !street;
    if (isZelArea && address.isManualInput) {
      const { street } = getManualZelAOAddressFieldsErrors({ address });
      return street.error;
    }

    return address.isManualInput
      ? !streetIsFoundInCatalog
      : !suggestions.find(
          (suggest) => getAddressValueFromSuggest(suggest) === street
        );
  }
);

export const validateAddressHouseAndBuilding: (state: {
  address: AddressFields;
}) => boolean = createSelector(
  getAddress,
  isZelArea,
  getAddressHouse,
  getAddressBuilding,
  (address, isZelArea, house, building) => {
    if (address.isAddressCompleted) return false;
    if (isZelArea && address.isManualInput) {
      const { house } = getManualZelAOAddressFieldsErrors({ address });
      return house.error;
    }
    return ![house, building].some(Boolean);
  }
);

export const validateAddressAddress: (state: {
  address: AddressFields;
}) => boolean = createSelector(
  getIsAddressCompleted,
  (isAddressCompleted) => !isAddressCompleted
);

export const validateAddressOMK: (address: AddressFields) => boolean = (
  address
) => {
  const state = { person: { address } };
  return (
    isOMKErrorVisible(state, { model: 'person' }) ||
    isAddressSubsequentErrorVisible(state, { model: 'person' })
  );
};

export const getManualAddress: (address: AddressFields) => string = (
  address
) => {
  const addressFieldNames = [
    { name: 'district', title: 'Поселение' },
    { name: 'street', title: '' },
    { name: 'house', title: 'Дом' },
    { name: 'structure', title: 'Строение' },
    { name: 'building', title: 'Корпус' },
    { name: 'flat', title: 'Квартира/офис' },
  ];
  const manualAddressString = addressFieldNames
    .reduce(
      (acc: string[], field: { name: string; title: string }): string[] => {
        if (field.name === 'district') {
          const district = getTextValue(address.district);
          const area = getTextValue(address.area);
          if (isNewMoscowArea(area) && district) {
            const title = district.startsWith('Городской округ')
              ? ''
              : field.title;
            const computedString = `${title} ${district}`.trim();
            acc.push(computedString);
          }
          return acc;
        }

        if (address[field.name].value) {
          const computedString = `${field.title} ${
            address[field.name].value
          }`.trim();
          acc.push(computedString);
        }
        return acc;
      },
      []
    )
    .join(', ');
  return `Москва, ${manualAddressString}`;
};
export const getManualZelAOAddressFieldsErrors = createSelector(
  getAddressSuggestions,
  getAddressStreet,
  getAddressHouse,
  getAddressBuilding,
  getAddressStreetIsFoundInCatalog,
  (suggestions, street, house, building, streetIsFoundInCatalog) => {
    const streetError = setErrorField(
      street.trim().length ? !streetIsFoundInCatalog : false
    );
    if (house) {
      return {
        house: setErrorField(false),
        building: setErrorField(false),
        street: setErrorField(!streetIsFoundInCatalog),
      };
    }
    if (building) {
      return {
        house: setErrorField(false),
        building: setErrorField(false),
        street: streetError,
      };
    }
    if (street) {
      return {
        house: setErrorField(!house),
        building: setErrorField(!house),
        street: streetError,
      };
    }

    return {
      street: setErrorField(false),
      house: setErrorField(!house),
      building: setErrorField(!building),
    };
  }
);

export const getManualAddressFieldsErrors = createSelector(
  getAddress,
  isZelArea,
  (address, isZelAO) => {
    return Object.assign(
      {
        area: setErrorField(validateAddressArea({ address })),
        district: setErrorField(validateAddressDistrict({ address })),
        flat: setErrorField(validateAddressFlat({ address })),
      },
      isZelAO
        ? getManualZelAOAddressFieldsErrors({ address })
        : {
            street: setErrorField(validateAddressStreet({ address })),
            house: setErrorField(validateAddressHouseAndBuilding({ address })),
            building: setErrorField(
              validateAddressHouseAndBuilding({ address })
            ),
          }
    );
  }
);

export const getAddressAddressError = createSelector(
  getAddress,
  ({ suggestions, address: addressValue, isAddressCompleted }) => {
    if (isAddressCompleted) return false;
    return {
      address: setErrorField(
        !suggestions.find(({ address }) => address === getValue(addressValue))
      ),
    };
  }
);

export const getAddressFieldsErrors = createSelector(getAddress, (address) => {
  return address.isManualInput
    ? getManualAddressFieldsErrors({ address })
    : getAddressAddressError({ address });
});
